import { ref, computed } from 'vue';
import { Logger } from '@/mixins/logger';
import connectorService from '@/services/connector.service';

const CACHE_KEY_PREFIX = 'pathway-catalog';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Shared state across all instances
const catalog = ref(null);
const loading = ref(false);
const error = ref(null);
const lastFetched = ref(null);
const currentPartnerId = ref(null);

/**
 * Resolve the current learner/partner context.
 * A signed-in learner is identified by a contactId or asuRite on learnerData; the
 * public (anonymous) catalog has neither.
 * @returns {{ hasLearnerSession: boolean, partnerId: (string|null) }}
 */
function getPartnerContext() {
  const learnerData = connectorService.learnerData;
  const hasLearnerSession = !!(
    learnerData &&
    (learnerData._contactId || learnerData.contactId || learnerData._asuRite || learnerData.asuRite)
  );
  const partnerId = learnerData?.partnerId || null;
  return { hasLearnerSession, partnerId };
}

/**
 * Get the cache key for the current partner.
 * Returns null for a signed-in learner whose partner has not resolved yet so we never
 * read or write the unfiltered (all-partner) catalog on their behalf (ULC-3156).
 * @returns {string|null} Cache key, or null when caching must be skipped
 */
function getCacheKey() {
  const { hasLearnerSession, partnerId } = getPartnerContext();
  if (hasLearnerSession && !partnerId) return null;
  return partnerId ? `${CACHE_KEY_PREFIX}-${partnerId}` : CACHE_KEY_PREFIX;
}

/**
 * Get cached catalog data from localStorage
 * @returns {Object|null} Cached data if valid, null otherwise
 */
function getCachedData() {
  try {
    const cacheKey = getCacheKey();
    if (!cacheKey) return null;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age < CACHE_TTL) {
      return { data, age, fromCache: true };
    }

    // Cache expired, remove it
    localStorage.removeItem(cacheKey);
    return null;
  } catch (err) {
    Logger.methods.logError('usePathwayCatalog - Error reading cache', err);
    return null;
  }
}

/**
 * Save catalog data to localStorage
 * @param {Object} data - Catalog data to cache
 */
function setCachedData(data) {
  try {
    const cacheKey = getCacheKey();
    if (!cacheKey) return;
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  } catch (err) {
    Logger.methods.logError('usePathwayCatalog - Error saving to cache', err);
  }
}

/**
 * Composable for fetching and caching pathway catalog data
 * Uses localStorage with 24-hour TTL for client-side caching
 */
export function usePathwayCatalog() {
  /**
   * Fetch pathway catalog from API or cache
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   * @returns {Promise<Object>} Catalog data
   */
  const fetchCatalog = async (forceRefresh = false) => {
    const { hasLearnerSession, partnerId } = getPartnerContext();

    // ULC-3156: A signed-in learner must only ever see pathways their partner is
    // authorized for. If their partner has not resolved yet, do not fall back to the
    // unfiltered all-partner catalog; wait for the partner-scoped fetch instead.
    if (hasLearnerSession && !partnerId) {
      return catalog.value && currentPartnerId.value ? catalog.value : { pathways: [], totalCount: 0 };
    }

    // A loaded catalog that belongs to a different partner context - including a stale
    // anonymous/all-partner catalog now that a partner is known - is no longer valid.
    const partnerChanged = catalog.value !== null && currentPartnerId.value !== partnerId;
    if (partnerChanged) {
      forceRefresh = true;
    }

    // Return in-memory data if available and not forcing refresh
    if (catalog.value && !forceRefresh) {
      return catalog.value;
    }

    // Check localStorage cache
    if (!forceRefresh) {
      const cached = getCachedData();
      if (cached) {
        catalog.value = cached.data;
        lastFetched.value = new Date(Date.now() - cached.age);
        currentPartnerId.value = partnerId;
        return cached.data;
      }
    }

    // Fetch from API
    loading.value = true;
    error.value = null;

    try {
      // Use the connector service which handles API key and auth headers centrally
      const data = await connectorService.getPathwayCatalog();
      catalog.value = data;
      lastFetched.value = new Date();
      currentPartnerId.value = partnerId;

      // Save to localStorage
      setCachedData(data);

      return data;
    } catch (err) {
      Logger.methods.logError('usePathwayCatalog - Error fetching catalog', err);
      error.value = err.message || 'Failed to fetch pathway catalog';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Clear the catalog cache (both in-memory and localStorage)
   */
  const clearCache = () => {
    catalog.value = null;
    lastFetched.value = null;
    currentPartnerId.value = null;
    const cacheKey = getCacheKey();
    if (cacheKey) localStorage.removeItem(cacheKey);
  };

  /**
   * Get a specific pathway by ID
   * @param {string} pathwayId - Pathway ID to find
   * @returns {Object|null} Pathway data or null if not found
   */
  const getPathwayById = (pathwayId) => {
    if (!catalog.value?.pathways) return null;
    return catalog.value.pathways.find((p) => String(p.pathwayId) === String(pathwayId));
  };

  /**
   * Get pathways filtered by interest area
   * @param {string} interestArea - Interest area to filter by
   * @returns {Array} Filtered pathways
   */
  const getPathwaysByInterestArea = (interestArea) => {
    if (!catalog.value?.pathways) return [];
    return catalog.value.pathways.filter(
      (p) => p.interestArea && p.interestArea.toLowerCase().includes(interestArea.toLowerCase())
    );
  };

  /**
   * Search pathways by name or description
   * @param {string} query - Search query
   * @returns {Array} Matching pathways
   */
  const searchPathways = (query) => {
    if (!catalog.value?.pathways || !query) return catalog.value?.pathways || [];

    const lowerQuery = query.toLowerCase();
    return catalog.value.pathways.filter(
      (p) =>
        (p.pathwayName && p.pathwayName.toLowerCase().includes(lowerQuery)) ||
        (p.pathwayDescription && p.pathwayDescription.toLowerCase().includes(lowerQuery)) ||
        (p.interestArea && p.interestArea.toLowerCase().includes(lowerQuery))
    );
  };

  // Computed properties
  const pathways = computed(() => catalog.value?.pathways || []);
  const totalCount = computed(() => catalog.value?.totalCount || 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => !!error.value);
  const errorMessage = computed(() => error.value);
  const isCached = computed(() => !!getCachedData());

  // Get unique interest areas for filtering
  const interestAreas = computed(() => {
    if (!catalog.value?.pathways) return [];
    const areas = new Set();
    catalog.value.pathways.forEach((p) => {
      if (p.interestArea) areas.add(p.interestArea);
    });
    return Array.from(areas).sort();
  });

  return {
    // State
    catalog,
    pathways,
    totalCount,
    loading: isLoading,
    error: errorMessage,
    hasError,
    lastFetched,
    isCached,
    interestAreas,

    // Methods
    fetchCatalog,
    clearCache,
    getPathwayById,
    getPathwaysByInterestArea,
    searchPathways
  };
}
